import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Lock, ArrowRight, ShieldAlert } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Gates the internal zone (members hub + forum) by dues status.
 *
 * Calls `GET /api/me/internal-access`. While the global enforcement toggle is
 * OFF (default) the backend always returns allowed=true, so this is a no-op.
 * When a member is blocked it renders a friendly "regulariza tu membresía"
 * screen instead of the protected content.
 */
export default function InternalAccessGuard({ children }) {
  const { lang } = useParams();
  const [state, setState] = useState({ loading: true, allowed: true, reason: '' });

  useEffect(() => {
    let alive = true;
    api.get('/me/internal-access')
      .then(({ data }) => { if (alive) setState({ loading: false, allowed: !!data.allowed, reason: data.reason || '' }); })
      .catch(() => { if (alive) setState({ loading: false, allowed: true, reason: '' }); }); // fail-open
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="internal-access-loading">
        <Loader2 className="animate-spin text-cmic-500" size={30} />
      </div>
    );
  }

  if (state.allowed) return children;

  return (
    <div className="min-h-[70vh] bg-gray-50 flex items-center justify-center px-4 pt-24 pb-16" data-testid="internal-access-blocked">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center mx-auto mb-5">
          <Lock size={30} />
        </div>
        <h1 className="text-2xl font-bold text-charcoal-900 mb-2">Zona interna restringida</h1>
        <p className="text-charcoal-600 mb-1 flex items-center justify-center gap-1.5">
          <ShieldAlert size={16} className="text-amber-500" /> {state.reason || 'Tu cuota no está al corriente'}
        </p>
        <p className="text-charcoal-500 text-sm mb-7">
          Para acceder al foro y a los recursos exclusivos de miembros, regulariza tu membresía.
          Si crees que es un error, contacta a la mesa directiva.
        </p>
        <Link
          to={`/${lang || 'es'}/mi-perfil`}
          data-testid="internal-access-renew-btn"
          className="inline-flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white shadow-md rounded-lg px-7 py-3 font-bold transition-all transform hover:-translate-y-0.5"
        >
          Ver mi membresía <ArrowRight size={17} />
        </Link>
      </div>
    </div>
  );
}
